import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    HostListener,
    inject,
    input,
    output,
} from '@angular/core';

import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';

import {NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {
    combineLatest,
    filter,
    map,
    merge,
    Observable,
    shareReplay,
    startWith,
    switchMap,
    take,
} from 'rxjs';
import {HasUnsavedChanges} from '@brunata-angular/ui';
import {
    AppValidators,
    filterNullishRx,
    filterNulls,
    FormValue,
    fromControl,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {selectBuilding} from 'src/app/state/selectors';
import {Store} from '@ngrx/store';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {EventAlarmsService} from '../event-alarms.service';
import {InputNumberGroupComponent} from '@brunata-angular/ui';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {ToastrService} from 'ngx-toastr';
import {ControlMessagesComponent, LoadComponent} from '@brunata-angular/ui';
import {
    EventAlarmCreateRequest,
    EventAlarmFormGroup,
    EventAlarmUpdateRequest,
    VendorModelsFormGroup,
} from '../event-alarm.model';
import {InputEmailListComponent} from '@brunata-angular/ui';
import {Duration} from 'luxon';
import {SelectVendorModelsComponent} from './select-vendor-models/select-vendor-models.component';
import {ConfirmationDialogService} from '@brunata-angular/ui';

type CreateEventAlarmView = {
    buildingNo: string;
    eventTypeId: string;
    vendorModels: FormValue<VendorModelsFormGroup>[];
    formGroup: EventAlarmFormGroup;
    allEventTypesUnsupported: boolean;
};

@Component({
    selector: 'app-create-event-alarm',
    templateUrl: './create-event-alarm.component.html',
    styleUrl: './create-event-alarm.component.scss',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        InputNumberGroupComponent,
        InputTextGroupComponent,
        TranslatePipe,
        LoadComponent,
        ControlMessagesComponent,
        InputEmailListComponent,
        SelectVendorModelsComponent,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateEventAlarmComponent implements HasUnsavedChanges {
    eventTypeId = input<string | undefined>();
    initialFormValues = input<Partial<FormValue<EventAlarmFormGroup>> | undefined>();

    updateEventAlarm = output<EventAlarmUpdateRequest>();
    unsavedChanges = output<boolean>();

    private readonly store = inject(Store);
    private readonly translateService = inject(TranslateService);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly eventAlarmService = inject(EventAlarmsService);
    private readonly toastrService = inject(ToastrService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly eventAlarmsService = inject(EventAlarmsService);

    private readonly initialFormValues$ = toObservable(this.initialFormValues);

    private readonly formGroup: EventAlarmFormGroup = this.formBuilder.group({
        submitted: [false],
        throttleInHours: this.formBuilder.control<number | null>(null, [
            Validators.required,
            AppValidators.numberBetween(0, 100, 'hourlyPeriodBetween'),
        ]),
        alarmName: this.formBuilder.control<string>('', [
            Validators.required,
            Validators.maxLength(200),
        ]),
        recipients: this.formBuilder.control<string[]>([], [Validators.required]),
        vendorModels: this.formBuilder.control<FormValue<VendorModelsFormGroup>[]>(
            [],
            Validators.required,
        ),
    });
    protected readonly subscriptionId$ = this.activatedRoute.paramMap.pipe(
        map(params => params.get('id')),
    );
    private readonly buildingNo$ = this.store.select(selectBuilding).pipe(
        filter(state => state.status === 'success'),
        map(({buildingNo}) => buildingNo),
        shareReplay(1),
    );

    private readonly eventTypeId$ = merge(
        toObservable(this.eventTypeId),
        this.activatedRoute.paramMap.pipe(map(params => params.get('eventtypeid'))),
    ).pipe(filterNullishRx(), shareReplay(1));

    private readonly vendorModels$ = combineLatest({
        buildingNo: this.buildingNo$,
        eventTypeId: this.eventTypeId$,
        initialModels: this.initialFormValues$.pipe(
            map(alarm => alarm?.vendorModels),
            filterNullishRx(),
            startWith([]),
        ),
    }).pipe(
        switchMap(({buildingNo, eventTypeId, initialModels}) =>
            this.eventAlarmService.getVendorModelsForEventType(buildingNo, eventTypeId).pipe(
                map(apiModels => {
                    const initialModelIds = new Set(initialModels.map(m => m.vendorModelId));
                    const supportedModels = apiModels.map(model => ({
                        ...model,
                        selected: initialModelIds.has(model.vendorModelId),
                        isEventTypeSupported: true,
                    }));
                    const supportedModelIds = new Set(supportedModels.map(m => m.vendorModelId));
                    const unsupportedModels = initialModels
                        .filter(model => !supportedModelIds.has(model.vendorModelId))
                        .map(model => ({...model, selected: true, isEventTypeSupported: false}));
                    return [...supportedModels, ...unsupportedModels];
                }),
            ),
        ),
        shareReplay(1),
    );

    private readonly vendorModelsWithSelection$ = combineLatest([
        this.vendorModels$,
        this.formGroup.controls.vendorModels.valueChanges.pipe(
            startWith(this.formGroup.controls.vendorModels.value),
        ),
    ]).pipe(
        map(([vendorModels, selectedVendorModels]) => {
            const selectedModelIds = new Set(selectedVendorModels.map(m => m.vendorModelId));

            return this.formGroup.pristine && selectedVendorModels.length === 0
                ? vendorModels
                : vendorModels.map(model => ({
                      ...model,
                      selected: selectedModelIds.has(model.vendorModelId),
                  }));
        }),
        shareReplay(1),
    );

    readonly view$: Observable<CreateEventAlarmView> = combineLatest({
        buildingNo: this.buildingNo$,
        eventTypeId: this.eventTypeId$,
        vendorModels: this.vendorModelsWithSelection$,
        formGroup: this.formGroup.valueChanges.pipe(
            startWith(this.formGroup),
            map(() => this.formGroup),
        ),
        allEventTypesUnsupported: this.vendorModelsWithSelection$.pipe(
            map(vendorModels => vendorModels.every(model => model.isEventTypeSupported === false)),
        ),
    });

    constructor() {
        // set initial values
        this.initialFormValues$
            .pipe(
                filterNullishRx(),
                switchMap(editEventAlarm =>
                    this.vendorModels$.pipe(
                        map(vendorModels => ({
                            throttleInHours: editEventAlarm.throttleInHours,
                            alarmName: editEventAlarm.alarmName,
                            recipients: editEventAlarm.recipients,
                            vendorModels: vendorModels.filter(model => model.selected),
                            submitted: false,
                        })),
                    ),
                ),
                take(1),
            )
            .subscribe(formValues => this.formGroup.patchValue(formValues, {emitEvent: false}));

        fromControl(this.formGroup)
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.formGroup.controls.submitted.setValue(false, {onlySelf: true}));

        // emit unsaved changes state
        this.router.events
            .pipe(
                takeUntilDestroyed(),
                filter(event => event instanceof NavigationStart),
            )
            .subscribe(() => this.unsavedChanges.emit(this.hasUnsavedChanges()));
    }

    submit(buildingNo: string, eventTypeId: string): void {
        this.formGroup.markAllAsTouched();

        if (this.formGroup.invalid) {
            return;
        }

        this.formGroup.controls.submitted.setValue(true);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {submitted, throttleInHours, vendorModels, ...rest} = this.formGroup.getRawValue();

        const throttle = Duration.fromObject({hours: Number(throttleInHours)}).as('minutes');
        const supportedModels = vendorModels.filter(model => model.isEventTypeSupported);

        if (supportedModels.length === 0) {
            this.subscriptionId$.pipe(take(1), filterNulls()).subscribe(subscriptionId => {
                this.deleteEventAlarm(
                    buildingNo,
                    this.formGroup.getRawValue().alarmName,
                    subscriptionId,
                    this.translateService.translate(
                        'event-alarms_No_supported_vendorModels_prompt_delete_alarm',
                    ),
                );
            });
            return;
        }

        const vendorModelIds = supportedModels.map(model => model.vendorModelId);
        const request = {...rest, throttle, vendorModelIds};

        // edit
        if (this.initialFormValues() !== undefined) {
            this.updateEventAlarm.emit(request);
            return;
        }

        this.createEventAlarm(buildingNo, {...request, eventTypeId: Number(eventTypeId)});
    }

    createEventAlarm(buildingNo: string, request: EventAlarmCreateRequest): void {
        void this.eventAlarmService.createEventAlarm(buildingNo, request).subscribe(() => {
            const successMessage = this.translateService
                .translate('event-alarms_alarm_created')
                .replace('{0}', this.formGroup.getRawValue().alarmName);

            this.toastrService.success(successMessage);

            this.back(buildingNo);
        });
    }

    resetForm(formGroup: EventAlarmFormGroup): void {
        formGroup.reset();
    }

    onVendorModelsChange(vendorModels: FormValue<VendorModelsFormGroup>[]): void {
        this.formGroup.controls.vendorModels.setValue(vendorModels.filter(model => model.selected));
        this.formGroup.controls.vendorModels.markAsTouched();
    }

    back(buildingNo: string): void {
        void this.router.navigate(['admin', 'system', buildingNo, 'monitoring', 'event-alarms']);
    }

    deleteEventAlarm(
        buildingNo: string,
        alarmName: string,
        subscriptionId: string,
        text?: string,
    ): void {
        void this.confirmationDialogService
            .confirm(
                this.translateService.translate('common_warning', 'online', 'common'),
                text != null
                    ? text
                    : this.translateService
                          .translate('monitoring_alarm_delete_warning')
                          .replace('{0}', alarmName),
                this.translateService.translate('common_Delete'),
            )
            .then(confirmed => {
                if (!confirmed) {
                    return;
                }

                this.eventAlarmsService
                    .deleteEventAlarm(buildingNo, subscriptionId)
                    .subscribe(() => {
                        this.toastrService.success(
                            this.translateService.translate('event-alarms_delete_success'),
                        );
                        this.back(buildingNo);
                    });
            });
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (!this.hasUnsavedChanges()) {
            return;
        }

        event.preventDefault();
    }

    hasUnsavedChanges(): boolean {
        return this.formGroup.touched && this.formGroup.value.submitted === false;
    }
}
